<template>
  <div class="tab-shell">
    <div class="tab-head">
      <h2 class="tab-title">Gerenciar Escritório</h2>
    </div>

    <div v-if="loading" class="empty-hint">Carregando...</div>
    <div v-else class="settings-card">
      <h3 class="settings-title">Encaminhamento de Requisições de Compra</h3>
      <p class="settings-desc">
        As requisições de compra são enviadas em planilha Excel para o email configurado abaixo,
        conforme a frequência escolhida.
      </p>

      <label class="field">
        <span class="field-label">Email de destino</span>
        <input class="field-input" v-model="forwardEmail" type="email" placeholder="compras@empresa.com.br" />
      </label>

      <div class="field">
        <span class="field-label">Frequência de envio</span>
        <div class="freq-options">
          <label class="freq-option" :class="{ active: forwardFrequency === 'sempre' }">
            <input type="radio" value="sempre" v-model="forwardFrequency" class="sr-only" />
            <span class="freq-name">Sempre</span>
            <span class="freq-desc">Sempre que uma nova requisição for solicitada</span>
          </label>
          <label class="freq-option" :class="{ active: forwardFrequency === 'diario' }">
            <input type="radio" value="diario" v-model="forwardFrequency" class="sr-only" />
            <span class="freq-name">Diariamente</span>
            <span class="freq-desc">Resumo diário com as requisições novas</span>
          </label>
          <label class="freq-option" :class="{ active: forwardFrequency === 'semanal' }">
            <input type="radio" value="semanal" v-model="forwardFrequency" class="sr-only" />
            <span class="freq-name">Semanalmente</span>
            <span class="freq-desc">Resumo semanal com as requisições novas</span>
          </label>
        </div>
      </div>

      <p v-if="lastForwardAt" class="settings-meta">Último envio: {{ formatDateTime(lastForwardAt) }}</p>
      <p v-if="formError" class="form-error">{{ formError }}</p>

      <div class="form-actions">
        <button class="btn-save" type="button" :disabled="saving" @click="save">
          {{ saving ? 'Salvando...' : 'Salvar' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue'
import { apiFetch } from '@/store/api'
import { uiStore } from '@/store/ui'
import { formatDateTimeTz } from '@/store/timezone'

interface OfficeSettings {
  forwardEmail: string
  forwardFrequency: 'sempre' | 'diario' | 'semanal'
  lastForwardAt: string
}

export default defineComponent({
  name: 'GerenciaEscritorio',
  setup() {
    const loading = ref(true)
    const saving = ref(false)
    const formError = ref('')
    const forwardEmail = ref('')
    const forwardFrequency = ref<'sempre' | 'diario' | 'semanal'>('sempre')
    const lastForwardAt = ref('')

    const load = async () => {
      loading.value = true
      try {
        const data = await apiFetch<OfficeSettings>('/office/settings')
        forwardEmail.value = data.forwardEmail || ''
        forwardFrequency.value = data.forwardFrequency || 'sempre'
        lastForwardAt.value = data.lastForwardAt || ''
      } catch {
        // mantém defaults
      } finally { loading.value = false }
    }

    const save = async () => {
      formError.value = ''
      const email = forwardEmail.value.trim()
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        formError.value = 'Informe um email válido.'
        return
      }
      saving.value = true
      try {
        await apiFetch('/office/settings', {
          method: 'POST',
          body: JSON.stringify({ forwardEmail: email, forwardFrequency: forwardFrequency.value })
        })
        uiStore.toast('Configurações salvas.', 'success')
      } catch (e: unknown) {
        formError.value = e instanceof Error ? e.message : 'Erro ao salvar.'
      } finally { saving.value = false }
    }

    const formatDateTime = formatDateTimeTz

    onMounted(load)

    return { loading, saving, formError, forwardEmail, forwardFrequency, lastForwardAt, save, formatDateTime }
  }
})
</script>

<style scoped lang="scss">
.tab-shell { width: 100%; }
.tab-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.tab-title { margin: 0; font-size: 18px; font-weight: 800; color: var(--ink, #2c2b27); }
.empty-hint { padding: 60px 20px; color: var(--ink-muted, #aaa); text-align: center; font-size: 14px; }
.settings-card {
  background: var(--card, #fff); border: 1px solid rgba(31,30,26,.1);
  border-radius: 14px; padding: 20px; max-width: 640px;
  display: flex; flex-direction: column; gap: 16px;
}
.settings-title { margin: 0; font-size: 15px; font-weight: 700; color: var(--ink, #2c2b27); }
.settings-desc { margin: -8px 0 0; font-size: 13px; color: var(--ink-muted, #888); line-height: 1.5; }
.field { display: flex; flex-direction: column; gap: 6px; }
.field-label { font-size: 12px; font-weight: 600; color: var(--ink-muted, #888); }
.field-input {
  padding: 8px 11px; border-radius: 7px; border: 1.5px solid rgba(31,30,26,.18);
  font-size: 13px; outline: none; background: #fff;
  &:focus { border-color: var(--system-navbar, #0f4c81); }
}
.freq-options { display: flex; flex-direction: column; gap: 8px; }
.freq-option {
  display: flex; flex-direction: column; gap: 2px; cursor: pointer;
  padding: 10px 14px; border-radius: 10px; border: 1.5px solid rgba(31,30,26,.14);
  transition: border-color .12s, background .12s;
  &:hover { background: rgba(31,30,26,.03); }
  &.active {
    border-color: var(--system-navbar, #0f4c81);
    background: rgba(15,76,129,.05);
    .freq-name { color: var(--system-navbar, #0f4c81); }
  }
}
.freq-name { font-size: 13px; font-weight: 700; color: var(--ink, #2c2b27); }
.freq-desc { font-size: 12px; color: var(--ink-muted, #888); }
.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); border: 0; }
.settings-meta { margin: 0; font-size: 12px; color: var(--ink-muted, #999); }
.form-error { font-size: 12px; color: #dc2626; margin: 0; }
.form-actions { display: flex; gap: 8px; }
.btn-save {
  padding: 8px 18px; border-radius: 7px; border: none; cursor: pointer;
  background: var(--system-navbar, #0f4c81); color: #fff; font-size: 13px; font-weight: 600;
  &:disabled { opacity: .6; cursor: not-allowed; }
  &:hover:not(:disabled) { opacity: .88; }
}
</style>
